import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "./styles.module.css";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  FaCog,
  FaTrash,
  FaPlus,
  FaBuilding,
  FaRegCalendarAlt,
} from "react-icons/fa";
import InvoiceNumberingSettings from "./InvoiceNumberingSettings";
import {
  generateInvoiceNumber,
  calculateDueDate,
  calculateTotals,
  validateItem,
  formatCurrency,
  formatNumber,
  getNextInvoiceCounter,
  validateInvoiceNumber,
  prepareInvoiceData,
  filterEntitiesByType,
  filterProductsByType,
} from "./invoiceHelpers";

const Forms = ({
  onCancel,
  type: initialType,
  token,
  userData,
  setShouldRefetch,
  preSelectedEntity,
  formatDate,
  returnTo,
}) => {
  const [t, i18n] = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  // Core state
  const [invoiceType, setInvoiceType] = useState(initialType || "purchase");

  // Entities state
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);

  // Warehouse state
  const [warehouses, setWarehouses] = useState([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState([]); // Warehouses with selected product (for sales)
  const [loadingProductWarehouses, setLoadingProductWarehouses] = useState(false);

  // Invoice numbering state
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceCounter, setInvoiceCounter] = useState(1);
  const savedNumberingMode =
    localStorage.getItem("invoiceNumberingMode") || "auto";
  const [numberingMode, setNumberingMode] = useState(savedNumberingMode);
  const [showNumberingSettings, setShowNumberingSettings] = useState(false);

  // Dates and terms
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [terms, setTerms] = useState("on_receipt");
  const [dueDate, setDueDate] = useState(null);
  const [customDueDate, setCustomDueDate] = useState("");

  // Discount state
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [discountType, setDiscountType] = useState("FIXED");
  const [discountValue, setDiscountValue] = useState(0);

  // Products and items
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    product: null,
    quantity: 1,
    pricePerUnit: 0,
    locationId: null, // For both purchase and sale invoices
  });

  // Track original warehouse quantities and allocated quantities
  const [warehouseAllocations, setWarehouseAllocations] = useState({});

  // Multiple images upload
  const [images, setImages] = useState([]);
  const [files, setFiles] = useState([]);

  // Validation errors
  const [invoiceNumberError, setInvoiceNumberError] = useState("");
  const [dateError, setDateError] = useState("");
  const [entityError, setEntityError] = useState("");

  // Loading states
  const [submitting, setSubmitting] = useState(false);

  // Calculate totals
  const totals = calculateTotals(
    items,
    discountEnabled,
    discountValue,
    discountType,
    false,
    0,
    "PERCENTAGE"
  );

  // Fetch entities, products and warehouses on mount
  useEffect(() => {
    fetchEntities();
    fetchProducts();
    fetchWarehouses();
    fetchNextInvoiceNumber();

    if (preSelectedEntity) {
      setSelectedEntity(preSelectedEntity);
    }
  }, []);

  // Update due date when terms or invoice date changes
  useEffect(() => {
    if (terms !== "custom") {
      const calculatedDueDate = calculateDueDate(date, terms);
      setDueDate(calculatedDueDate);
    }
  }, [terms, date]);

  // Reset warehouse allocations when invoice type changes
  useEffect(() => {
    // Only reset if actually changing the type
    setWarehouseAllocations({});
    setItems([]); // Clear items when switching between purchase/sale
    setFilteredWarehouses([]);
    setCurrentItem({ product: null, quantity: 1, pricePerUnit: 0, locationId: null });
  }, [invoiceType]);

  // Fetch entities function
  const fetchEntities = async () => {
    try {
      const response = await axios.get(
        `api/entitiesAccounts/getByFarmId/${userData.Farm?.id}`,
        { headers: { Authorization: token } }
      );
      const allEntities = response.data.items || response.data || [];
      setEntities(allEntities);
    } catch (error) {
      console.error("Error fetching entities:", error);
      try {
        const [suppliersRes, clientsRes] = await Promise.all([
          axios.get(`api/supplier/findByFarmId/${userData.Farm?.id}`, {
            headers: { Authorization: token },
          }),
          axios.get(`api/client/findByFarmId/${userData.Farm?.id}`, {
            headers: { Authorization: token },
          }),
        ]);

        const suppliers =
          suppliersRes.data.items?.map((s) => ({
            ...s,
            accountType: "SUPPLIER",
          })) || [];
        const clients =
          clientsRes.data.items?.map((c) => ({
            ...c,
            accountType: "CLIENT",
          })) || [];
        setEntities([...suppliers, ...clients]);
      } catch (fallbackError) {
        console.error("Error fetching entities from fallback:", fallbackError);
      }
    }
  };

  // Fetch products function
  const fetchProducts = async () => {
    try {
      const response = await axios.get(
        `api/inventoryItemDetails/findByFarmId/${userData.Farm?.id}`,
        { headers: { Authorization: token } }
      );
      setProducts(response.data.items || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  // Fetch warehouses function
  const fetchWarehouses = async () => {
    try {
      const response = await axios.get(
        `api/inventoryLocation/findByFarmId/${userData.Farm?.id}`,
        { headers: { Authorization: token } }
      );
      setWarehouses(response.data.items || []);
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  // Calculate available quantity for a warehouse considering items already in the list
  const getAdjustedWarehouseQuantity = (warehouseId, productId, originalQuantity) => {
    if (!warehouseId || !productId) return originalQuantity || 0;

    // Calculate total quantity already allocated to this warehouse/product in items list
    const allocatedQty = items
      .filter(item => item.locationId === warehouseId && item.product.id === productId)
      .reduce((sum, item) => sum + item.quantity, 0);

    return Math.max(0, (originalQuantity || 0) - allocatedQty);
  };

  // Filter warehouses based on product selection (for sales) - matching AddMovement.js logic
  const filterWarehousesByProduct = async (product) => {
    if (!product || !token || invoiceType !== "sale") {
      setFilteredWarehouses([]);
      return;
    }

    setLoadingProductWarehouses(true);

    try {
      // Use the same endpoint as AddMovement.js: inventoryLocation/findByFarmId/{farmId}?itemId={itemId}
      const response = await axios.get(
        `api/inventoryLocation/findByFarmId/${userData.Farm?.id}`,
        {
          params: { itemId: product.id },
          headers: { Authorization: token }
        }
      );

      const productWarehouses = response.data.items || [];

      if (productWarehouses.length > 0) {
        // For each warehouse that has the product, fetch the actual quantity
        const warehousesWithQuantities = await Promise.all(
          productWarehouses.map(async (warehouse) => {
            try {
              // Fetch items in this location to get quantities
              const itemsResponse = await axios.get(
                `api/inventoryItemsAllocation/location/${warehouse.id}`,
                { headers: { Authorization: token } }
              );

              const items = itemsResponse.data.items || [];
              // Find the current product in the items list
              const currentItem = items.find(item => item.id === product.id);
              const originalQuantity = currentItem ? (currentItem.currentBalance || 0) : 0;

              // Store original quantity for this warehouse/product combination if not already stored
              const allocationKey = `${warehouse.id}_${product.id}`;

              // Use existing allocation if available, otherwise store the new one
              let storedOriginalQty = originalQuantity;
              setWarehouseAllocations(prev => {
                if (allocationKey in prev) {
                  storedOriginalQty = prev[allocationKey];
                  return prev; // Already have this allocation stored
                }
                return { ...prev, [allocationKey]: originalQuantity };
              });

              return {
                ...warehouse,
                quantity: Math.max(0, originalQuantity),
                originalQuantity: storedOriginalQty // Use stored value if switching back to this product
              };
            } catch (error) {
              console.log(`Could not fetch quantity for warehouse ${warehouse.id}`);
              return {
                ...warehouse,
                quantity: 0
              };
            }
          })
        );

        // Only show warehouses with available stock
        const warehousesWithStock = warehousesWithQuantities.filter(w => w.quantity > 0);
        setFilteredWarehouses(warehousesWithStock);
      } else {
        // No warehouses have this product
        setFilteredWarehouses([]);
      }
    } catch (error) {
      console.log("Could not fetch product-specific warehouses");
      setFilteredWarehouses([]);
    } finally {
      setLoadingProductWarehouses(false);
    }
  };

  // Fetch next invoice number
  const fetchNextInvoiceNumber = async (forceGenerate = false) => {
    try {
      const counter = await getNextInvoiceCounter(userData.Farm?.id, token);
      setInvoiceCounter(counter);

      const currentMode =
        localStorage.getItem("invoiceNumberingMode") || "auto";

      if (currentMode === "auto" || forceGenerate) {
        const prefix = localStorage.getItem("invoicePrefix") || "INV-";
        const includeYear =
          localStorage.getItem("invoiceIncludeYear") === "true";
        const generatedNumber = generateInvoiceNumber(
          prefix,
          includeYear,
          counter
        );
        setInvoiceNumber(generatedNumber);
      }

      return counter;
    } catch (error) {
      console.error("Error fetching next invoice counter:", error);
      return 1;
    }
  };

  // Handle invoice type change
  const handleInvoiceTypeChange = (newType) => {
    if (newType !== invoiceType) {
      setInvoiceType(newType);
      setSelectedEntity(null);
      setItems([]);
      setCurrentItem({ product: null, quantity: 1, pricePerUnit: 0, locationId: null });
      setFilteredWarehouses([]);
    }
  };

  // Handle entity selection
  const handleEntitySelect = (entity) => {
    setSelectedEntity(entity);
    setEntityError("");
  };

  // Handle product selection
  const handleProductSelect = async (product) => {
    if (product) {
      const updatedItem = {
        ...currentItem,
        product: product,
        pricePerUnit: product.price || 0,
        locationId: null, // Reset warehouse selection when product changes
      };
      setCurrentItem(updatedItem);

      // For sales, fetch warehouses that have this product
      if (invoiceType === "sale") {
        await filterWarehousesByProduct(product);
      }
    }
  };

  // Handle warehouse selection for current item (both purchase and sale)
  const handleItemWarehouseSelect = (warehouseId) => {
    setCurrentItem(prev => ({ ...prev, locationId: warehouseId }));
  };

  // Handle adding item to list
  const handleAddItem = () => {
    const validation = validateItem(currentItem);
    if (!validation.valid) {
      toast.error(t(validation.message));
      return;
    }

    // Validate warehouse selection for both purchase and sale
    if (!currentItem.locationId) {
      toast.error(t("يرجى اختيار المستودع"));
      return;
    }

    // For sales only, validate stock availability
    if (invoiceType === "sale") {
      // Find the selected warehouse to check its stock
      const selectedWarehouse = filteredWarehouses.find(
        w => w.id === currentItem.locationId
      );

      if (!selectedWarehouse) {
        toast.error(t("المستودع المحدد غير صالح"));
        return;
      }

      // Get adjusted quantity considering items already in the list
      const adjustedQuantity = getAdjustedWarehouseQuantity(
        currentItem.locationId,
        currentItem.product.id,
        selectedWarehouse.originalQuantity || selectedWarehouse.quantity
      );

      if (currentItem.quantity > adjustedQuantity) {
        toast.error(
          t(`الكمية المتاحة في المستودع هي ${adjustedQuantity} فقط`)
        );
        return;
      }
    }

    const newItem = {
      ...currentItem,
      id: Date.now(),
      totalAmount: currentItem.quantity * currentItem.pricePerUnit,
    };

    setItems([...items, newItem]);
    setCurrentItem({ product: null, quantity: 1, pricePerUnit: 0, locationId: null });
    setFilteredWarehouses([]); // Clear warehouses when adding item
  };

  // Handle removing item
  const handleRemoveItem = (itemId) => {
    setItems(items.filter((item) => item.id !== itemId));
    // The available quantity will be automatically recalculated via getAdjustedWarehouseQuantity
  };

  // Handle terms change
  const handleTermsChange = (e) => {
    const newTerms = e.target.value;
    setTerms(newTerms);

    if (newTerms === "custom") {
      setDueDate(null);
    }
  };

  // Handle custom due date change
  const handleCustomDueDateChange = (e) => {
    const dateValue = e.target.value;
    setCustomDueDate(dateValue);
    setDueDate(new Date(dateValue).getTime());
  };

  // Handle invoice numbering settings save
  const handleNumberingSettingsSave = async (settings) => {
    setNumberingMode(settings.mode);
    localStorage.setItem("invoiceNumberingMode", settings.mode);

    if (settings.mode === "auto") {
      setInvoiceNumber(settings.invoiceNumber);
      setInvoiceCounter(settings.counter);
    } else if (settings.isManual) {
      setInvoiceNumber("");
    }
  };

  // Validate invoice number
  const validateInvoiceNumberAPI = async (number) => {
    try {
      const isValid = await validateInvoiceNumber(
        userData.Farm?.id,
        number,
        token
      );
      return isValid;
    } catch (error) {
      return true;
    }
  };

  // Handle multiple images upload
  const handleImageChange = (e) => {
    const uploadedFiles = Array.from(e.target.files);
    if (uploadedFiles.length > 0) {
      const newImages = [];
      const newFiles = [...files];

      uploadedFiles.forEach((file) => {
        newFiles.push(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          newImages.push(reader.result);
          if (newImages.length === uploadedFiles.length) {
            setImages([...images, ...newImages]);
          }
        };
        reader.readAsDataURL(file);
      });

      setFiles(newFiles);
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    const newFiles = files.filter((_, i) => i !== index);
    setImages(newImages);
    setFiles(newFiles);
  };

  // Form validation
  const validateForm = () => {
    let isValid = true;
    let errors = [];

    setInvoiceNumberError("");
    setDateError("");
    setEntityError("");

    if (!selectedEntity) {
      const entityTypeText = invoiceType === "purchase" ? t("مورد") : t("عميل");
      setEntityError(t("يرجى اختيار") + " " + entityTypeText);
      errors.push(t("يرجى اختيار") + " " + entityTypeText);
      isValid = false;
    }

    if (!invoiceNumber) {
      setInvoiceNumberError(t("رقم الفاتورة مطلوب"));
      errors.push(t("رقم الفاتورة مطلوب"));
      isValid = false;
    }

    if (!date) {
      setDateError(t("تاريخ الفاتورة مطلوب"));
      errors.push(t("تاريخ الفاتورة مطلوب"));
      isValid = false;
    }

    if (items.length === 0) {
      errors.push(t("يرجى إضافة منتج واحد على الأقل"));
      isValid = false;
    }

    // Validate warehouse per item for both purchase and sale invoices
    const itemsWithoutWarehouse = items.filter(item => !item.locationId);
    if (itemsWithoutWarehouse.length > 0) {
      errors.push(t("يرجى اختيار المستودع لجميع المنتجات"));
      isValid = false;
    }

    if (terms === "custom" && !customDueDate) {
      errors.push(t("تاريخ الاستحقاق مطلوب للشروط المخصصة"));
      isValid = false;
    }

    // Show all errors in a single toast if validation fails
    if (!isValid && errors.length > 0) {
      toast.error(
        <div>
          <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
            {t("يرجى إكمال الحقول المطلوبة:")}
          </div>
          {errors.map((error, index) => (
            <div key={index} style={{ marginLeft: "10px" }}>
              • {error}
            </div>
          ))}
        </div>,
        { duration: 4000 }
      );
    }

    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (numberingMode === "manual") {
      const isValid = await validateInvoiceNumberAPI(invoiceNumber);
      if (!isValid) {
        setInvoiceNumberError(t("This invoice number already exists"));
        return;
      }
    }

    setSubmitting(true);

    try {
      const invoiceData = prepareInvoiceData(
        {
          invoiceType,
          selectedEntity,
          selectedWarehouse: null, // No longer used, locationId is per item
          invoiceNumber,
          invoiceCounter,
          date,
          terms,
          dueDate:
            terms === "custom" ? new Date(customDueDate).getTime() : dueDate,
          items,
          discountEnabled,
          discountValue,
          discountType,
          taxEnabled: false,
          taxRate: 0,
          taxType: "PERCENTAGE",
          totals,
          companyInfo: {},
        },
        userData
      );

      // Ensure dates are numbers, not strings
      invoiceData.invoiceDate = Number(invoiceData.invoiceDate);
      if (invoiceData.dueDate) {
        invoiceData.dueDate = Number(invoiceData.dueDate);
      }

      const formData = new FormData();
      formData.append("invoice", JSON.stringify(invoiceData));

      // Check total file size before upload
      if (files.length > 0) {
        const totalSize = files.reduce((sum, file) => sum + file.size, 0);
        const maxSizeInMB = 10; // 10MB limit to be safe
        const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

        if (totalSize > maxSizeInBytes) {
          const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
          toast.error(
            t(
              `حجم الصور كبير جداً (${totalSizeMB}MB). الحد الأقصى ${maxSizeInMB}MB. يرجى ضغط الصور أو تقليل عددها`
            )
          );
          setSubmitting(false);
          return;
        }

        // Send multiple images with the same parameter name (like Android)
        files.forEach((file) => {
          formData.append("imageFile", file);
        });
      }

      await axios.post("api/invoice/addInvoiceWithImage", formData, {
        headers: {
          Authorization: token,
          "Content-Type": "multipart/form-data",
        },
      });

      // On successful submission, handle next invoice number
      if (numberingMode === "auto") {
        const nextCounter = invoiceCounter + 1;
        setInvoiceCounter(nextCounter);

        const prefix = localStorage.getItem("invoicePrefix") || "INV-";
        const includeYear =
          localStorage.getItem("invoiceIncludeYear") === "true";
        const nextNumber = generateInvoiceNumber(
          prefix,
          includeYear,
          nextCounter
        );
        setInvoiceNumber(nextNumber);
      } else {
        setInvoiceNumber("");

        const savedType = localStorage.getItem("invoiceNumberGenerationType");
        if (savedType === "manual_once") {
          localStorage.setItem("invoiceNumberGenerationType", "auto");
          setNumberingMode("auto");
          await fetchNextInvoiceNumber();
        }
      }

      toast.success(t("Invoice added successfully"));

      // Reset form fields
      setSelectedEntity(null);
      setItems([]);
      setCurrentItem({ product: null, quantity: 1, pricePerUnit: 0, locationId: null });
      setImages([]);
      setFiles([]);
      setDiscountEnabled(false);
      setDiscountValue(0);
      setDate(new Date().toISOString().split("T")[0]);
      setTerms("on_receipt");
      setCustomDueDate("");

      setShouldRefetch((prev) => !prev);

      // Navigate to returnTo location if coming from entity profile, otherwise return to invoice list
      if (returnTo) {
        navigate(returnTo, { state: { entity: preSelectedEntity, activeTab: "invoices" } });
      } else {
        // Call onCancel to switch back to list view within the invoices page
        if (onCancel) {
          onCancel();
        }
      }
    } catch (error) {
      console.error("Error adding invoice:", error);
      toast.error(t("Error adding invoice"));
    } finally {
      setSubmitting(false);
    }
  };

  // Get filtered entities and products based on invoice type
  const filteredEntities = filterEntitiesByType(entities, invoiceType);
  const filteredProducts = filterProductsByType(products, invoiceType);

  return (
    <div className={styles.invoiceFormContainer}>
      <div className={styles.formCard}>
        <header className={styles.viewHeader}>
          <h1 className={styles.viewHeaderTitleGroup}>{t("إنشاء فاتورة")}</h1>
        </header>

        <form onSubmit={handleSubmit} className={styles.invoiceForm}>
          {/* Invoice Type Toggle Section */}
          <div className={styles.invoiceTypeSection}>
            <div className={styles.typeToggle}>
              <button
                type="button"
                className={invoiceType === "purchase" ? styles.active : ""}
                onClick={() => handleInvoiceTypeChange("purchase")}
              >
                {t("شراء")}
              </button>
              <button
                type="button"
                className={invoiceType === "sale" ? styles.active : ""}
                onClick={() => handleInvoiceTypeChange("sale")}
              >
                {t("بيع")}
              </button>
            </div>
          </div>

          {/* Entity and Invoice Number Row */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="entity">
                {invoiceType === "sale" ? t("عميل") : t("مورد")} *
              </label>
              <div className={styles.inputWithIcon}>
                <FaBuilding className={styles.inputIcon} />
                <select
                  id="entity"
                  name="entity"
                  value={selectedEntity?.id || ""}
                  onChange={(e) => {
                    const entity = filteredEntities.find(
                      (ent) => ent.id == e.target.value
                    );
                    handleEntitySelect(entity);
                  }}
                  required
                >
                  <option value="" disabled>
                    {t("اختر")}
                  </option>
                  {filteredEntities.map((entity, index) => (
                    <option key={index} value={entity.id}>
                      {entity.name}
                    </option>
                  ))}
                </select>
              </div>
              {entityError && (
                <span className={styles.errorText}>{entityError}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="invoiceNumber">{t("رقم الفاتورة")} *</label>
              <div className={styles.invoiceNumberWrapper}>
                <input
                  type="text"
                  id="invoiceNumber"
                  name="invoiceNumber"
                  value={invoiceNumber}
                  onChange={(e) => {
                    setInvoiceNumber(e.target.value);
                    setInvoiceNumberError("");
                  }}
                  placeholder={
                    numberingMode === "auto"
                      ? "INV-00001"
                      : t("أدخل رقم الفاتورة")
                  }
                  disabled={numberingMode === "auto"}
                  required
                />
                <button
                  type="button"
                  className={styles.settingsButton}
                  onClick={async () => {
                    const counter = await getNextInvoiceCounter(
                      userData.Farm?.id,
                      token
                    );
                    setInvoiceCounter(counter);
                    setShowNumberingSettings(true);
                  }}
                  aria-label={t("Invoice number settings")}
                >
                  <FaCog />
                </button>
              </div>
              {invoiceNumberError && (
                <span className={styles.errorText}>{invoiceNumberError}</span>
              )}
            </div>
          </div>

          {/* Date and Terms Row */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="date">{t("التاريخ")} *</label>
              <div className={styles.inputWithIcon}>
                <FaRegCalendarAlt className={styles.inputIcon} />
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={date}
                  onChange={(e) => {
                    setDate(e.target.value);
                    setDateError("");
                  }}
                  required
                />
              </div>
              {dateError && (
                <span className={styles.errorText}>{dateError}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="terms">{t("شروط الدفع")}</label>
              <select
                id="terms"
                name="terms"
                value={terms}
                onChange={handleTermsChange}
              >
                <option value="on_receipt">{t("فوري")}</option>
                <option value="15">{t("15 يوم")}</option>
                <option value="45">{t("45 يوم")}</option>
                <option value="90">{t("90 يوم")}</option>
                <option value="custom">{t("مخصص")}</option>
              </select>
            </div>
          </div>

          {/* Custom Due Date */}
          {terms === "custom" && (
            <div className={styles.formGroup}>
              <label htmlFor="customDueDate">{t("تاريخ الاستحقاق")} *</label>
              <div className={styles.inputWithIcon}>
                <FaRegCalendarAlt className={styles.inputIcon} />
                <input
                  type="date"
                  id="customDueDate"
                  name="customDueDate"
                  value={customDueDate}
                  onChange={handleCustomDueDateChange}
                  min={date}
                  required
                />
              </div>
            </div>
          )}


          {/* Discount Section */}
          <div className={styles.formGroupInline}>
            <input
              type="checkbox"
              id="discountEnabled"
              name="discountEnabled"
              checked={discountEnabled}
              onChange={(e) => setDiscountEnabled(e.target.checked)}
            />
            <label htmlFor="discountEnabled">{t("إضافة خصم")}</label>
          </div>

          {discountEnabled && (
            <div className={styles.discountOptionsWrapper}>
              <div className={styles.discountTypeToggle}>
                <button
                  type="button"
                  className={discountType === "FIXED" ? styles.active : ""}
                  onClick={() => setDiscountType("FIXED")}
                >
                  {t("ثابت")}
                </button>
                <button
                  type="button"
                  className={discountType === "PERCENTAGE" ? styles.active : ""}
                  onClick={() => setDiscountType("PERCENTAGE")}
                >
                  {t("نسبة مئوية")}
                </button>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="discountValue">{t("قيمة الخصم")}</label>
                <input
                  type="number"
                  id="discountValue"
                  name="discountValue"
                  value={discountValue}
                  onChange={(e) =>
                    setDiscountValue(parseFloat(e.target.value) || 0)
                  }
                  placeholder={discountType === "PERCENTAGE" ? "0%" : "0.00"}
                  min="0"
                  max={discountType === "PERCENTAGE" ? "100" : undefined}
                />
              </div>
            </div>
          )}

          {/* Products Section */}
          <div className={styles.productsSection}>
            <h3 className={styles.sectionTitle}>{t("المنتجات")}</h3>

            {/* Add Product Row */}
            <div className={`${styles.addItemRow} ${styles.withWarehouse}`}>
              <div className={styles.productSelect}>
                <label>{t("المنتج")}</label>
                <select
                  value={currentItem.product?.id || ""}
                  onChange={(e) => {
                    const product = filteredProducts.find(
                      (p) => p.id == e.target.value
                    );
                    handleProductSelect(product);
                  }}
                >
                  <option value="" disabled>
                    {t("اختر المنتج")}
                  </option>
                  {filteredProducts.map((product, index) => {
                    // Calculate total quantity already added for this product
                    const addedQuantity = items
                      .filter(item => item.product.id === product.id)
                      .reduce((sum, item) => sum + item.quantity, 0);

                    const productName = (isRTL ? product.nameAr : product.nameEn) || product.name;
                    const displayText = addedQuantity > 0
                      ? `${productName} (${addedQuantity} ${t("مضاف")})`
                      : productName;

                    return (
                      <option key={index} value={product.id}>
                        {displayText}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Warehouse Selection - For both purchase and sale */}
              <div className={styles.warehouseSelect}>
                <label>{t("المستودع")}</label>
                <select
                  value={currentItem.locationId || ""}
                  onChange={(e) => handleItemWarehouseSelect(parseInt(e.target.value))}
                  disabled={!currentItem.product || (invoiceType === "sale" && loadingProductWarehouses)}
                >
                  <option value="" disabled>
                    {!currentItem.product
                      ? t("اختر المنتج أولاً")
                      : invoiceType === "sale" && loadingProductWarehouses
                      ? t("جاري التحميل...")
                      : invoiceType === "sale" && filteredWarehouses.length === 0
                      ? t("لا توجد مستودعات بها مخزون")
                      : t("اختر المستودع")}
                  </option>
                  {invoiceType === "sale" ? (
                    filteredWarehouses.map((warehouse) => {
                      const adjustedQty = getAdjustedWarehouseQuantity(
                        warehouse.id,
                        currentItem.product?.id,
                        warehouse.originalQuantity || warehouse.quantity
                      );
                      // Only show warehouses that still have available stock
                      if (adjustedQty <= 0) return null;

                      return (
                        <option key={warehouse.id} value={warehouse.id}>
                          {isRTL ? warehouse.nameAr : warehouse.nameEn} ({adjustedQty} {t("متاح")})
                        </option>
                      );
                    }).filter(Boolean)
                  ) : (
                    warehouses.map((warehouse) => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {isRTL ? warehouse.nameAr : warehouse.nameEn}
                      </option>
                    ))
                  )}
                </select>
                {invoiceType === "sale" && currentItem.product && currentItem.locationId && (
                  <small className={styles.stockInfo}>
                    {t("الكمية المتاحة")}: {
                      (() => {
                        const warehouse = filteredWarehouses.find(w => w.id === currentItem.locationId);
                        return warehouse ? getAdjustedWarehouseQuantity(
                          currentItem.locationId,
                          currentItem.product.id,
                          warehouse.originalQuantity || warehouse.quantity
                        ) : 0;
                      })()
                    }
                  </small>
                )}
              </div>

              <div className={styles.quantityInput}>
                <label>{t("الكمية")}</label>
                <input
                  type="number"
                  value={currentItem.quantity}
                  onChange={(e) =>
                    setCurrentItem({
                      ...currentItem,
                      quantity: parseFloat(e.target.value) || 0,
                    })
                  }
                  min="0"
                  step="0.01"
                />
              </div>

              <div className={styles.priceInput}>
                <label>{t("السعر لكل وحدة")}</label>
                <input
                  type="number"
                  value={currentItem.pricePerUnit}
                  onChange={(e) =>
                    setCurrentItem({
                      ...currentItem,
                      pricePerUnit: parseFloat(e.target.value) || 0,
                    })
                  }
                  min="0"
                  step="0.01"
                />
              </div>

              <div className={styles.unitDisplay}>
                <label>{t("الوحدة")}</label>
                <span>{currentItem.product?.unit || "—"}</span>
              </div>

              <div className={styles.itemTotalDisplay}>
                <label>{t("المجموع")}</label>
                <span>
                  {formatCurrency(
                    currentItem.quantity * currentItem.pricePerUnit,
                    i18n.language
                  )}
                </span>
              </div>

              <button
                type="button"
                className={styles.addItemBtn}
                onClick={handleAddItem}
              >
                <FaPlus />
              </button>
            </div>

            {/* Product Summary (shows allocated quantities per warehouse) */}
            {items.length > 0 && (
              <div className={styles.productSummary}>
                <h4>{t("ملخص المنتجات المضافة")}</h4>
                <div className={styles.summaryGrid}>
                  {Object.entries(
                    items.reduce((acc, item) => {
                      const productKey = item.product.id;
                      if (!acc[productKey]) {
                        acc[productKey] = {
                          product: item.product,
                          warehouses: {}
                        };
                      }
                      const warehouseName = warehouses.find(w => w.id === item.locationId);
                      const warehouseKey = item.locationId;
                      if (!acc[productKey].warehouses[warehouseKey]) {
                        acc[productKey].warehouses[warehouseKey] = {
                          name: warehouseName ? (isRTL ? warehouseName.nameAr : warehouseName.nameEn) : t("Unknown"),
                          quantity: 0
                        };
                      }
                      acc[productKey].warehouses[warehouseKey].quantity += item.quantity;
                      return acc;
                    }, {})
                  ).map(([productId, data]) => (
                    <div key={productId} className={styles.summaryItem}>
                      <span className={styles.productName}>
                        {(isRTL ? data.product.nameAr : data.product.nameEn) || data.product.name}:
                      </span>
                      {Object.entries(data.warehouses).map(([warehouseId, info]) => (
                        <span key={warehouseId} className={styles.warehouseInfo}>
                          {info.name}: {info.quantity}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Items Table */}
            {items.length > 0 && (
              <div className={styles.productsTable}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("المنتج")}</th>
                      <th>{t("المستودع")}</th>
                      <th>{t("الكمية")}</th>
                      <th>{t("السعر")}</th>
                      <th>{t("المجموع")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const warehouse = warehouses.find(w => w.id === item.locationId);
                      return (
                        <tr key={item.id}>
                          <td>
                            {(isRTL
                              ? item.product.nameAr
                              : item.product.nameEn) || item.product.name}
                          </td>
                          <td>
                            {warehouse
                              ? (isRTL ? warehouse.nameAr : warehouse.nameEn)
                              : "—"}
                          </td>
                          <td>{formatNumber(item.quantity, i18n.language)}</td>
                          <td>
                            {formatCurrency(item.pricePerUnit, i18n.language)}
                          </td>
                          <td>
                            {formatCurrency(item.totalAmount, i18n.language)}
                          </td>
                          <td>
                            <button
                              type="button"
                              className={styles.deleteButton}
                              onClick={() => handleRemoveItem(item.id)}
                              aria-label={t("حذف")}
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Images Upload Section */}
          <div className={styles.attachmentsSection}>
            <input
              type="file"
              accept="image/*"
              id="image-upload"
              onChange={handleImageChange}
              style={{ display: "none" }}
              multiple
            />
            <div
              className={styles.imageUpload}
              onClick={() => document.getElementById("image-upload").click()}
            >
              <FaPlus />
              <span>{t("إضافة صور الفاتورة")}</span>
            </div>
          </div>

          <div className={styles.imagePreviews}>
            {images.map((image, index) => (
              <div key={index} className={styles.imagePreviewItem}>
                <img src={image} alt="preview" />
                <button
                  type="button"
                  className={styles.removeImageBtn}
                  onClick={() => handleRemoveImage(index)}
                >
                  <FaTrash />
                </button>
              </div>
            ))}
          </div>

          {/* Totals Section - Moved after images */}
          {items.length > 0 && (
            <div className={styles.totalsSection}>
              <div className={styles.totalRow}>
                <span>{t("المجموع الفرعي")}</span>
                <span>{formatCurrency(totals.subtotal, i18n.language)}</span>
              </div>

              {discountEnabled && (
                <div className={styles.totalRow}>
                  <span>
                    {t("الخصم")}
                    {discountType === "PERCENTAGE" && ` (${discountValue}%)`}
                  </span>
                  <span>-{formatCurrency(totals.discount, i18n.language)}</span>
                </div>
              )}

              <div className={`${styles.totalRow} ${styles.grandTotal}`}>
                <span>{t("الإجمالي")}</span>
                <span>{formatCurrency(totals.total, i18n.language)}</span>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className={styles.formActions}>
            <button
              type="submit"
              className={styles.buttonPrimary}
              disabled={submitting}
            >
              {submitting ? t("جاري الإضافة...") : t("إضافة الفاتورة")}
            </button>

            <button
              type="button"
              className={styles.buttonSecondary}
              onClick={onCancel}
              disabled={submitting}
            >
              {t("إلغاء")}
            </button>
          </div>
        </form>
      </div>

      {/* Invoice Numbering Settings Modal */}
      <InvoiceNumberingSettings
        show={showNumberingSettings}
        onHide={() => setShowNumberingSettings(false)}
        onSave={handleNumberingSettingsSave}
        currentCounter={invoiceCounter}
        validateInvoiceNumber={validateInvoiceNumberAPI}
      />
    </div>
  );
};

export default Forms;
